import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSession, useUser } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { Envelope, ArrowUpRight, CheckCircle } from "@phosphor-icons/react";
import DashboardSection from "./DashboardSection";

interface AssignedTicket {
  id: number;
  title?: string;
  building?: { id?: string } | null;
  unreadEmailCount?: number;
}

/**
 * "Openstaande communicatie" module: helpdesk tickets assigned to the current
 * user that still have unread incoming emails — i.e. communication awaiting a
 * reply. Fully server-side filtered (assignee + hasUnreadEmails).
 *
 * Designed as a self-contained module so the dashboard can grow with more
 * outstanding-action sources later (e.g. tasks, approvals).
 */
const OutstandingCommunicationModule = () => {
  const { session } = useSession();
  const { user } = useUser();
  const [tickets, setTickets] = useState<AssignedTicket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAssigned = useCallback(async () => {
    if (!session || !user?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        assignee: user.id,
        hasUnreadEmails: "true",
        status: "open,completion",
        includeEmailCounts: "true",
        pageSize: "100",
        sortField: "id",
        sortDirection: "DESC",
      });
      const res = await fetchWithAuth(
        `${API_URL}/helpdesk/tickets?${params}`,
        { method: "GET" },
        session,
      );
      if (!res.ok) throw new Error(`Kon tickets niet ophalen (${res.status})`);
      const result = await res.json();
      setTickets(result.data ?? []);
    } catch (err) {
      console.error("Error fetching assigned tickets with unread emails:", err);
      setError(err instanceof Error ? err.message : "Fout bij ophalen");
    } finally {
      setIsLoading(false);
    }
  }, [session, user?.id]);

  useEffect(() => {
    fetchAssigned();
  }, [fetchAssigned]);

  return (
    <DashboardSection
      title="Openstaande communicatie"
      description="Helpdesk-tickets die aan jou zijn toegewezen met ongelezen e-mails"
      icon={<Envelope size={22} weight="duotone" />}
      count={tickets.length}
    >
      {isLoading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-neutral-secondary-mid">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-moss-primary border-t-transparent" />
          Laden…
        </div>
      ) : error ? (
        <p className="py-4 text-sm text-status-bad">{error}</p>
      ) : tickets.length === 0 ? (
        <div className="flex items-center gap-2 py-4 text-sm text-neutral-secondary-mid">
          <CheckCircle size={18} weight="duotone" className="text-status-good" />
          Geen openstaande communicatie — je bent helemaal bij!
        </div>
      ) : (
        <ul className="flex flex-col divide-y divide-neutral-primary/10">
          {tickets.map((ticket) => (
            <li key={ticket.id}>
              <Link
                to={`/helpdesk/${ticket.id}`}
                className="group flex items-center justify-between gap-3 py-3 transition-colors hover:bg-moss-primary/5"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-neutral-primary">
                    #{ticket.id} {ticket.title || "Ticket zonder titel"}
                  </p>
                  {ticket.building?.id && (
                    <p className="text-xs text-neutral-secondary-mid">
                      Gebouw {ticket.building.id}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  {!!ticket.unreadEmailCount && ticket.unreadEmailCount > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-brick-primary/10 px-2 py-0.5 text-xs font-medium text-brick-primary">
                      <Envelope size={13} weight="fill" />
                      {ticket.unreadEmailCount} ongelezen
                    </span>
                  )}
                  <ArrowUpRight
                    size={16}
                    className="text-neutral-secondary-mid transition-colors group-hover:text-moss-primary"
                  />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </DashboardSection>
  );
};

export default OutstandingCommunicationModule;
